// Cashflow timeline — when money actually moves, not just what totals.
//
// Conservative v1: revenue is computed at MIN prices so the projected
// trough is the worst-case working-capital gap. The chart annotation makes
// this explicit. We can add a max-price band in v2.

import { DEFAULT_PLANS, PLAN_LABELS, type PaymentPlan } from './cashflow-defaults';
import type { CashflowPlanKey, CostCategory, EventData } from './types';

/**
 * Look up the payment plan for a given key, preferring per-event overrides
 * and falling back to the bundled default.
 */
export function resolvePlan(data: EventData, key: CashflowPlanKey): PaymentPlan {
  const override = data.cashflowPlans?.[key];
  return override && override.length > 0 ? override : DEFAULT_PLANS[key];
}

type Movement = {
  date: string; // YYYY-MM-DD
  amount: number; // signed: + in, − out
  description: string;
};

export type WeekBucket = {
  weekStart: string;
  weekEnd: string;
  daysToEvent: number; // ISO Monday → event-date delta in days
  inflow: number;
  outflow: number;
  cumulative: number;
  movements: { description: string; amount: number }[];
};

export type Cashflow = {
  weeks: WeekBucket[];
  trough: { date: string; amount: number; daysToEvent: number } | null;
  totalIn: number;
  totalOut: number;
  finalNet: number;
  eventWeekStart: string | null;
};

export function computeCashflow(data: EventData, eventDateISO: string): Cashflow {
  const eventDate = parseDate(eventDateISO);
  const movements: Movement[] = [];

  const ticketPlan = resolvePlan(data, 'tickets');
  const sponsorPlan = resolvePlan(data, 'sponsors');
  const addonPlan = resolvePlan(data, 'addons');

  for (const t of data.tickets) {
    const total = t.qty * t.priceMin;
    if (total > 0) addPlanned(movements, total, ticketPlan, eventDate, `Tickets · ${t.name}`);
  }
  for (const s of data.sponsors) {
    const total = s.qty * s.priceMin;
    if (total > 0) addPlanned(movements, total, sponsorPlan, eventDate, `Sponsors · ${s.name}`);
  }
  for (const a of data.addons) {
    if (!a.enabled) continue;
    const m = a.multiplier && a.multiplier > 0 ? a.multiplier : 1;
    const total = a.price * m;
    if (total > 0) addPlanned(movements, total, addonPlan, eventDate, `Add-on · ${a.name}`);
  }
  for (const cat of Object.keys(data.costs) as CostCategory[]) {
    const plan = resolvePlan(data, cat);
    for (const item of data.costs[cat]) {
      if (item.amount > 0) {
        addPlanned(movements, -item.amount, plan, eventDate, `${labelOf(cat)} · ${item.name || 'item'}`);
      }
    }
  }

  // Bucket by ISO week (Monday-anchored).
  const weekMap = new Map<string, WeekBucket>();
  const eventMonday = formatDate(mondayOf(eventDate));
  for (const m of movements) {
    const monday = mondayOf(parseDate(m.date));
    const key = formatDate(monday);
    let b = weekMap.get(key);
    if (!b) {
      const sunday = new Date(monday);
      sunday.setUTCDate(monday.getUTCDate() + 6);
      b = {
        weekStart: key,
        weekEnd: formatDate(sunday),
        daysToEvent: Math.round((eventDate.getTime() - monday.getTime()) / 86_400_000),
        inflow: 0,
        outflow: 0,
        cumulative: 0,
        movements: [],
      };
      weekMap.set(key, b);
    }
    if (m.amount >= 0) b.inflow += m.amount;
    else b.outflow += -m.amount;
    b.movements.push({ description: m.description, amount: m.amount });
  }

  const weeks = Array.from(weekMap.values()).sort((a, b) => a.weekStart.localeCompare(b.weekStart));
  let running = 0;
  let trough: Cashflow['trough'] = null;
  for (const w of weeks) {
    running += w.inflow - w.outflow;
    w.cumulative = running;
    if (trough === null || running < trough.amount) {
      trough = { date: w.weekStart, amount: running, daysToEvent: w.daysToEvent };
    }
  }

  return {
    weeks,
    trough,
    totalIn: weeks.reduce((acc, w) => acc + w.inflow, 0),
    totalOut: weeks.reduce((acc, w) => acc + w.outflow, 0),
    finalNet: running,
    eventWeekStart: weeks.some((w) => w.weekStart === eventMonday) ? eventMonday : null,
  };
}

export function tDayLabel(daysToEvent: number): string {
  if (daysToEvent === 0) return 'event week';
  if (daysToEvent > 0) return `T-${daysToEvent}d`;
  return `T+${-daysToEvent}d`;
}

// ---- helpers --------------------------------------------------------------

function addPlanned(
  out: Movement[],
  total: number,
  plan: PaymentPlan,
  eventDate: Date,
  description: string,
) {
  for (const m of plan) {
    const date = new Date(eventDate);
    date.setUTCDate(date.getUTCDate() - m.daysBeforeEvent);
    out.push({ date: formatDate(date), amount: total * m.pct, description });
  }
}

function mondayOf(d: Date): Date {
  const m = new Date(d);
  const dow = m.getUTCDay(); // Sun=0..Sat=6
  const offset = dow === 0 ? -6 : 1 - dow;
  m.setUTCDate(m.getUTCDate() + offset);
  m.setUTCHours(0, 0, 0, 0);
  return m;
}

function parseDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function formatDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function labelOf(c: CostCategory): string {
  return PLAN_LABELS[c];
}
